import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { api } from '../../lib/api';
import type { LoginMethods } from '../../lib/types';
import { formatDateTime } from '../../lib/format';
import { hasRole, useMe, useSessionChanged } from '../../lib/session';
import { credentialToJson, isPasskeySupported, parseCreationOptions } from '../../lib/webauthn';
import { Alert, ErrorMessage, Field, Loading } from '../../components/ui';
import { GoogleButton } from '../../components/GoogleButton';

/** Login methods: password, Google, passkeys, authenticator app (TOTP) and recovery codes. */
export function SecuritySection() {
  const { data: me } = useMe();
  const isTherapist = hasRole(me, 'Therapist');
  const client = useQueryClient();
  const sessionChanged = useSessionChanged();
  const methods = useQuery({ queryKey: ['login-methods'], queryFn: () => api.get<LoginMethods>('/api/v1/account/login-methods') });
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, success: string) {
    setError(null);
    setMessage(null);
    try {
      await action();
      await sessionChanged();
      await client.invalidateQueries({ queryKey: ['login-methods'] });
      setMessage(success);
    } catch (e) {
      setError(e);
    }
  }

  async function addPasskey() {
    await run(async () => {
      const options = await api.post<Record<string, unknown>>('/api/v1/account/passkeys/options');
      const credential = (await navigator.credentials.create({ publicKey: parseCreationOptions(options) })) as PublicKeyCredential | null;
      if (!credential) throw new Error('cancelled');
      const name = window.prompt('Nazwa klucza (np. „Telefon”)', 'Klucz dostępu') ?? 'Klucz dostępu';
      await api.post('/api/v1/account/passkeys', { credential: credentialToJson(credential), name });
    }, 'Dodano klucz dostępu.');
  }

  if (!methods.data) return <Loading />;
  const m = methods.data;

  return (
    <section aria-labelledby="security-title" className="card">
      <h2 id="security-title">Metody logowania</h2>
      <ErrorMessage error={error} />
      {message && <Alert kind="success">{message}</Alert>}

      <h3>Klucze dostępu (passkeys)</h3>
      <p className="hint">Najwygodniejsza i najbezpieczniejsza metoda – logowanie odciskiem palca, twarzą lub PIN-em urządzenia.</p>
      <ul className="plain-list">
        {m.passkeys.map((p) => (
          <li key={p.id}>
            {p.name ?? 'Klucz dostępu'} <span className="muted">(dodany {formatDateTime(p.createdAt)})</span>{' '}
            <button type="button" className="link-button danger" onClick={() => void run(() => api.delete(`/api/v1/account/passkeys/${p.id}`), 'Usunięto klucz dostępu.')}>
              Usuń
            </button>
          </li>
        ))}
      </ul>
      {isPasskeySupported() && (
        <button type="button" className="secondary" onClick={() => void addPasskey()}>
          Dodaj klucz dostępu
        </button>
      )}

      <h3>Aplikacja uwierzytelniająca</h3>
      {m.totpEnabled ? <TotpEnabled run={run} recoveryCodesLeft={m.recoveryCodesLeft} /> : <TotpSetup onEnabled={run} />}

      <h3>Konto Google</h3>
      {m.googleLinked ? (
        <p>
          Połączone.{' '}
          {!isTherapist && (
            <button type="button" className="link-button danger" onClick={() => void run(() => api.delete('/api/v1/account/logins/google'), 'Odłączono konto Google.')}>
              Odłącz
            </button>
          )}
        </p>
      ) : m.googleAvailable ? (
        <GoogleButton href="/api/v1/auth/external/google?mode=link" label="Połącz z kontem Google" />
      ) : (
        <p className="muted">Logowanie przez Google nie jest włączone w tej placówce.</p>
      )}

      <h3>Hasło</h3>
      {isTherapist ? (
        <p className="muted">Terapeuci logują się wyłącznie przez Google – konto Google tworzy też spotkania wizyt online.</p>
      ) : (
        <PasswordForm hasPassword={m.hasPassword} run={run} />
      )}
    </section>
  );
}

type Run = (action: () => Promise<unknown>, success: string) => Promise<void>;

function TotpSetup({ onEnabled }: { onEnabled: Run }) {
  const setup = useQuery({
    queryKey: ['totp-setup'],
    queryFn: () => api.get<{ sharedKey: string; authenticatorUri: string }>('/api/v1/account/two-factor/totp'),
  });
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (setup.data) {
      void QRCode.toDataURL(setup.data.authenticatorUri, { width: 180, margin: 1 }).then(setQr);
    }
  }, [setup.data]);

  if (codes) return <RecoveryCodes codes={codes} />;
  if (!setup.data) return <Loading />;

  return (
    <div>
      <ol>
        <li>Zainstaluj aplikację uwierzytelniającą (np. Aegis, Google Authenticator, Microsoft Authenticator).</li>
        <li>
          Zeskanuj kod QR lub wpisz klucz: <code>{setup.data.sharedKey}</code>
        </li>
        <li>Wpisz 6-cyfrowy kod z aplikacji.</li>
      </ol>
      {qr && <img src={qr} width={180} height={180} alt="Kod QR do dodania konta w aplikacji uwierzytelniającej" />}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onEnabled(async () => {
            const result = await api.post<{ recoveryCodes: string[] }>('/api/v1/account/two-factor/totp', { code });
            setCodes(result.recoveryCodes);
          }, 'Włączono weryfikację dwuetapową.');
        }}
      >
        <Field label="Kod z aplikacji" name="totp" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} required />
        <button type="submit">Włącz</button>
      </form>
    </div>
  );
}

function TotpEnabled({ run, recoveryCodesLeft }: { run: Run; recoveryCodesLeft: number }) {
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  if (codes) return <RecoveryCodes codes={codes} />;
  return (
    <div>
      <p>
        Włączona. Pozostałe kody odzyskiwania: <strong>{recoveryCodesLeft}</strong>.
      </p>
      <Field label="Kod z aplikacji (do zmian)" name="totp-manage" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} />
      <div className="button-row">
        <button
          type="button"
          className="secondary"
          onClick={() =>
            void run(async () => {
              const result = await api.post<{ recoveryCodes: string[] }>('/api/v1/account/two-factor/recovery-codes', { code });
              setCodes(result.recoveryCodes);
            }, 'Wygenerowano nowe kody odzyskiwania.')
          }
        >
          Nowe kody odzyskiwania
        </button>
        <button type="button" className="danger" onClick={() => void run(() => api.post('/api/v1/account/two-factor/totp/disable', { code }), 'Wyłączono aplikację uwierzytelniającą.')}>
          Wyłącz
        </button>
      </div>
    </div>
  );
}

function RecoveryCodes({ codes }: { codes: string[] }) {
  return (
    <Alert kind="warning">
      <p>
        <strong>Zapisz kody odzyskiwania</strong> w bezpiecznym miejscu. Każdy działa jeden raz, gdy nie masz dostępu do aplikacji.
      </p>
      <ul className="codes">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

function PasswordForm({ hasPassword, run }: { hasPassword: boolean; run: Run }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void run(() => api.post('/api/v1/account/password', { currentPassword: current || null, newPassword: next }), 'Zapisano hasło.');
      }}
    >
      {hasPassword && <Field label="Obecne hasło" name="current" type="password" autoComplete="current-password" value={current} onChange={setCurrent} required />}
      <Field label={hasPassword ? 'Nowe hasło' : 'Ustaw hasło'} name="next" type="password" autoComplete="new-password" value={next} onChange={setNext} hint="Co najmniej 12 znaków." required />
      <div className="button-row">
        <button type="submit">{hasPassword ? 'Zmień hasło' : 'Ustaw hasło'}</button>
        {hasPassword && (
          <button type="button" className="danger" onClick={() => void run(() => api.delete('/api/v1/account/password'), 'Usunięto hasło.')}>
            Usuń hasło
          </button>
        )}
      </div>
    </form>
  );
}
